<template>
    <v-container class="clublink-galaxy">
      <v-card class="rounded-xl elevation-12 glass clublink-card">
        <v-card-title class="clublink-header">
          <div class="d-flex align-center">
            <v-avatar size="36" class="mr-3" style="background: linear-gradient(135deg, #d4af37, #c9a961);">
              <v-icon style="color: #2d4a3e;">mdi-briefcase-plus</v-icon>
            </v-avatar>
            <div>
              <div class="title font-weight-bold white--text" style="font-family: 'Playfair Display', serif;">Products</div>
              <div class="caption white--text" style="opacity:.85">Create, polish, and track your offerings</div>
            </div>
          </div>
          <v-spacer />
          <v-text-field
            v-model="search" dense outlined hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Search products"
            class="mr-3 search"
          />
          <v-btn color="#d4af37" style="color: #2d4a3e; font-weight: 600;" @click="openCreate">
            <v-icon left>mdi-plus</v-icon> New Product
          </v-btn>
        </v-card-title>
  
        <v-divider class="gold-divider"/>
  
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="filtered"
            :loading="loading"
            item-key="id"
            dense
            class="rounded-xl product-table"
            loading-text="Loading products…"
            @click:row="openWorkspace"
          >
            <!-- Name + summary + avatar -->
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="32" class="mr-3" :color="avatarColor(item.name)">
                  <span class="white--text">{{ initials(item.name) }}</span>
                </v-avatar>
                <div>
                  <div class="font-weight-medium dark-green--text">{{ item.name }}</div>
                  <div class="caption grey--text">{{ item.summary }}</div>
                </div>
              </div>
            </template>
  
            <!-- Status chip with icon -->
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="statusColor(item.status)" text-color="white">
                <v-icon left x-small>{{ statusIcon(item.status) }}</v-icon>
                {{ item.status }}
              </v-chip>
            </template>
  
            <!-- Pretty Updated time -->
            <template v-slot:item.updatedAt="{ item }">
              <div class="d-flex align-center">
                <v-icon left small class="gold--text">mdi-clock-outline</v-icon>
                <span>{{ prettyTs(item.updatedAt) }}</span>
                <v-chip x-small class="ml-2 gold-time-chip">
                  {{ since(item.updatedAt) }}
                </v-chip>
              </div>
            </template>
  
            <!-- Actions -->
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" @click.stop="openWorkspace(item)">
                    <v-icon class="gold--text">mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                <span>Open workspace</span>
              </v-tooltip>
  
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" @click.stop="edit(item)">
                    <v-icon class="gold--text">mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>
  
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon :loading="deletingId===item.id" v-bind="attrs" v-on="on" @click.stop="removeItem(item)">
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </template>
  
            <template v-slot:no-data>
              <div class="pa-6 text-center grey--text">
                <v-icon size="36" class="mb-2 gold--text">mdi-briefcase-search</v-icon>
                <div>No products yet. Click <strong>New Product</strong> to add your first.</div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
  
      <!-- Create/Edit dialog -->
      <v-dialog v-model="dlg" max-width="560">
        <v-card class="rounded-xl clublink-dialog">
          <v-card-title class="clublink-dialog-header">
            <v-icon left color="#d4af37">mdi-briefcase</v-icon>
            <span style="font-family: 'Playfair Display', serif;">{{ form.id ? 'Edit Product' : 'Create Product' }}</span>
            <v-spacer/>
            <v-chip x-small style="background: rgba(212, 175, 55, 0.2); color: #2d4a3e;">{{ form.slug || slugify(form.name) }}</v-chip>
          </v-card-title>
          <v-divider class="gold-divider"/>
          <v-card-text class="pa-6">
            <v-form ref="frm">
              <v-text-field 
                v-model="form.name" 
                label="Name" 
                outlined 
                dense 
                @input="autoSlug"
                class="clublink-input"
                color="#d4af37"
              />
              <v-text-field 
                v-model="form.slug" 
                label="Slug (unique)" 
                outlined 
                dense
                class="clublink-input"
                color="#d4af37"
              />
              <v-select 
                :items="['active','paused','archived']" 
                v-model="form.status" 
                label="Status" 
                outlined 
                dense
                class="clublink-input"
                color="#d4af37"
              />
              <v-textarea 
                v-model="form.summary" 
                label="Summary" 
                outlined 
                dense 
                auto-grow
                class="clublink-input"
                color="#d4af37"
              />
            </v-form>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer/>
            <v-btn text @click="dlg=false" class="gold--text">Cancel</v-btn>
            <v-btn color="#d4af37" dark :loading="saving" @click="save" class="clublink-btn">
              <v-icon left>mdi-content-save</v-icon> Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </template>
  
  <script>
  import { db } from '@/firebase'
  import {
    collection, addDoc, onSnapshot, orderBy, query, serverTimestamp,
    updateDoc, doc, deleteDoc
  } from 'firebase/firestore'
  
  export default {
    name: 'AdminProducts',
    data () {
      return {
        loading: true,
        products: [],
        unsub: null,
        search: '',
        dlg: false,
        saving: false,
        deletingId: '',
        form: { id: '', name: '', slug: '', summary: '', status: 'active' },
        headers: [
          { text:'Name', value:'name' },
          { text:'Status', value:'status', width:120 },
          { text:'Updated', value:'updatedAt', width:260 },
          { text:'', value:'actions', sortable:false, width:130 },
        ],
      }
    },
    created () {
      const qRef = query(collection(db,'products'), orderBy('updatedAt','desc'))
      this.unsub = onSnapshot(qRef, snap => {
        this.products = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.loading = false
      })
    },
    beforeDestroy () { if (this.unsub) this.unsub() },
    computed: {
      filtered () {
        const q = (this.search || '').toLowerCase()
        return this.products.filter(p =>
          [p.name, p.summary, p.slug].join(' ').toLowerCase().includes(q)
        )
      }
    },
    methods: {
      /* ---------- visuals ---------- */
      statusColor (s) {
        return s === 'active' ? '#4caf50'
             : s === 'paused' ? '#ff9800'
             : 'grey'
      },
      statusIcon (s) {
        return s === 'active' ? 'mdi-check-circle'
             : s === 'paused' ? 'mdi-pause-circle'
             : 'mdi-archive'
      },
      initials (name='') { return name.split(/\s+/).map(x=>x[0]).join('').slice(0,2).toUpperCase() },
      avatarColor (name='') {
        const colors = ['#2d4a3e','#1a2e26','#3d5a4e','#4a6a5e','#5a7a6e','#d4af37','#c9a961','#b8963c']
        const i = Math.abs(name.split('').reduce((a,c)=>a+c.charCodeAt(0),0)) % colors.length
        return colors[i]
      },
  
      /* ---------- time formatting ---------- */
      toDate (ts) {
        if (!ts) return null
        if (ts.toDate) return ts.toDate()
        if (ts.seconds) return new Date(ts.seconds * 1000)
        if (typeof ts === 'number') return new Date(ts)
        return null
      },
      prettyTs (ts) {
        const d = this.toDate(ts)
        return d ? d.toLocaleString(undefined, { month:'short', day:'2-digit', year:'numeric', hour:'numeric', minute:'2-digit' }) : '—'
      },
      since (ts) {
        const d = this.toDate(ts); if (!d) return '—'
        const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000))
        const units = [
          ['y', 31536000], ['mo', 2592000], ['d', 86400],
          ['h', 3600], ['m', 60], ['s', 1]
        ]
        for (const [label, sec] of units) {
          const v = Math.floor(s / sec)
          if (v >= 1) return `${v}${label} ago`
        }
        return 'just now'
      },
  
      /* ---------- slug helpers ---------- */
      slugify (str='') {
        return String(str).toLowerCase()
          .replace(/[^a-z0-9]+/g,'-')
          .replace(/(^-|-$)/g,'')
          .slice(0,64)
      },
      autoSlug () {
        if (!this.form.id && !this.form.slug) this.form.slug = this.slugify(this.form.name)
      },
  
      /* ---------- CRUD ---------- */
      openCreate () {
        this.form = { id:'', name:'', slug:'', summary:'', status:'active' }
        this.dlg = true
      },
      edit (item) {
        this.form = { ...item }
        this.dlg = true
      },
      async save () {
        this.saving = true
        try {
          const payload = {
            name: this.form.name,
            slug: this.form.slug || this.slugify(this.form.name),
            summary: this.form.summary,
            status: this.form.status || 'active',
            updatedAt: serverTimestamp(),
            visibility: 'team',
          }
          if (!this.form.id) {
            await addDoc(collection(db, 'products'), {
              ...payload,
              createdAt: serverTimestamp(),
            })
          } else {
            await updateDoc(doc(db, 'products', this.form.id), payload)
          }
          this.dlg = false
        } finally { this.saving = false }
      },
      async removeItem (item) {
        this.deletingId = item.id
        try { await deleteDoc(doc(db,'products', item.id)) }
        finally { this.deletingId = '' }
      },
  
      /* ---------- navigation ---------- */
      openWorkspace (item /*, event */) {
        // Works for row click (@click:row passes the row item) and for button click
        const id = item && item.id ? item.id : (item?.item?.id) // defensive
        if (!id) return
        // Prefer named route; fallback to path
        this.$router.push({ name: 'AdminProductDetail', params: { id } }).catch(() => {
          this.$router.push(`/admin/products/${id}`)
        })
      }
    }
  }
  </script>
  
  <style scoped>
  /* ClubLink Colors */
  .gold--text { color: #d4af37 !important; }
  .dark-green--text { color: #2d4a3e !important; }
  
  /* ClubLink Galaxy backdrop */
  .clublink-galaxy{
    position: relative;
    padding-top: 8px;
    background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 50%, #1a2e26 100%);
    overflow: hidden;
    min-height: 100vh;
  }
  
  .clublink-galaxy::before, .clublink-galaxy::after{
    content:''; position:absolute; inset:0; pointer-events:none;
  }
  
  .clublink-galaxy::before{
    background-image: 
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
      radial-gradient(2px 2px at 15% 25%, rgba(212, 175, 55, 0.6), transparent 60%),
      radial-gradient(1.5px 1.5px at 65% 15%, rgba(212, 175, 55, 0.5), transparent 60%),
      radial-gradient(1.8px 1.8px at 35% 75%, rgba(212, 175, 55, 0.4), transparent 60%);
    animation: twinkle 6s ease-in-out infinite alternate;
    opacity:.7;
  }
  
  @keyframes twinkle{ from{opacity:.45} to{opacity:.85} }
  
  /* Card "glass" feel */
  .glass{
    backdrop-filter: saturate(130%) blur(6px);
    background: rgba(255,255,255,.95);
  }
  
  .clublink-card {
    border-top: 4px solid #d4af37 !important;
    position: relative;
    overflow: hidden;
  }
  
  .clublink-card::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(212, 175, 55, 0.03));
    pointer-events: none;
  }
  
  /* Header gradient bar */
  .clublink-header{
    background: linear-gradient(135deg, #2d4a3e, #1a2e26);
    color:#fff;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
  }
  
  .gold-divider {
    border-color: rgba(212, 175, 55, 0.2) !important;
    border-width: 2px !important;
  }
  
  /* Table polish */
  .product-table >>> thead {
    background: rgba(212, 175, 55, 0.05) !important;
  }
  
  .product-table >>> thead th {
    color: #2d4a3e !important;
    font-weight: 600 !important;
  }
  
  .product-table >>> tbody tr { 
    cursor: pointer;
  }
  
  .product-table >>> tbody tr:hover { 
    background: rgba(212, 175, 55, 0.08) !important;
  }
  
  .gold-time-chip {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(201, 169, 97, 0.1)) !important;
    color: #2d4a3e !important;
    border: 1px solid rgba(212, 175, 55, 0.3);
  }
  
  .search{ max-width: 320px; }
  .rounded-xl{ border-radius:16px; }
  
  /* Dialog */
  .clublink-dialog {
    border-top: 4px solid #d4af37 !important;
  }
  
  .clublink-dialog-header {
    background: linear-gradient(135deg, #2d4a3e, #1a2e26) !important;
    color: white;
  }
  
  /* Form Inputs */
  .clublink-input >>> .v-input__slot {
    background-color: #f8f6f1 !important;
    border-radius: 8px !important;
  }
  
  .clublink-input >>> fieldset {
    border-color: #e0e0e0 !important;
    border-width: 2px !important;
  }
  
  .clublink-input >>> .v-input--is-focused fieldset {
    border-color: #d4af37 !important;
  }
  
  /* Buttons */
  .clublink-btn {
    font-family: 'Montserrat', sans-serif !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
  }
  
  .clublink-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.4) !important;
  }
  </style>